import { useEffect, useState } from 'react'

import ArrowLeftIcon from '@heroicons/react/solid/ArrowLeftIcon'
import SearchIcon from '@heroicons/react/solid/SearchIcon'
import dayjs from 'dayjs'
import { useHistory } from 'react-router'
import { Link } from 'react-router-dom'

import { PickerFormControl } from '@tribeplatform/react-sdk/components'
import {
  SystemSpaceSlug,
  useMember,
  useSearch,
  useSpace,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { EmptyState } from '@tribeplatform/react-ui-kit/EmptyState'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Container, GridList, List } from '@tribeplatform/react-ui-kit/Layout'
import { PageHeader } from '@tribeplatform/react-ui-kit/PageHeader'
import { Tabs } from '@tribeplatform/react-ui-kit/Tabs'
import { useQuery } from '@tribeplatform/slate-kit/utils'

import { useDebounce } from '../../utils/useDebounce'
import { cleanupSearchResult } from './SearchBar'
import { SearchLoading } from './SearchLoading'
import { SearchRow } from './SearchRow'

const SearchNoResult = () => {
  return (
    <Card className="py-10">
      <EmptyState
        icon={<SearchIcon />}
        title="No results"
        description="You may want to try searching for something else"
      />
    </Card>
  )
}

const SearchResult = ({ isLoading, data, type }) => {
  return (
    <>
      {isLoading && <SearchLoading multisection={!type} />}
      {!isLoading && !data?.totalCount && <SearchNoResult />}
      {cleanupSearchResult(data)?.hits.map(category => (
        <div key={category.entityType}>
          {!type && (
            <div className="px-2 text-basicMain-900 capitalize font-medium text-xl pb-2">
              {category.entityType}
            </div>
          )}
          <Card className="mb-5">
            <List spacing="none" divider>
              {category?.hits.map(hit => {
                return (
                  <List.Item key={hit.id} className="hover:bg-surface-100">
                    <SearchRow row={hit} category={category} />
                  </List.Item>
                )
              })}
            </List>
          </Card>
        </div>
      ))}
    </>
  )
}

const SpacePicker = ({ space, replacePath }) => {
  return (
    <PickerFormControl.SpacePicker
      name="space"
      value={space}
      label="Posted in"
      placeholder="Pick a space..."
      onChange={space => {
        replacePath({
          spaceId: space?.id || null,
        })
      }}
    />
  )
}

const MemberPicker = ({ member, replacePath }) => {
  return (
    <PickerFormControl.MemberPicker
      value={member}
      name="member"
      label="Posted by"
      placeholder="Pick a member..."
      onChange={member => {
        replacePath({
          memberId: member?.id || null,
        })
      }}
    />
  )
}

const DatePicker = ({ date, replacePath }) => {
  return (
    <FormControl.Select
      name="date"
      value={date}
      label="Created at"
      placeholder="Pick a timeframe..."
      onChange={date => {
        replacePath({
          date: date || null,
        })
      }}
      items={[
        {
          text: 'Any time',
          value: undefined,
        },
        {
          text: 'Today',
          value: '1',
        },
        {
          text: 'Last 7 days',
          value: '7',
        },
        {
          text: 'Last 30 days',
          value: '30',
        },
        {
          text: 'Last 3 months',
          value: '92',
        },
        {
          text: 'Last 12 months',
          value: '365',
        },
      ]}
    />
  )
}

export const Search = () => {
  const { replace } = useHistory()
  const { generatePath } = useSpaceUtil()

  const {
    type,
    member: memberId,
    space: spaceId,
    date,
    query,
  } = useQuery() || {}

  const { data: space } = useSpace({
    fields: { image: 'basic' },
    variables: { id: spaceId },
    useQueryOptions: {
      enabled: !!spaceId,
    },
  })
  const { data: member } = useMember({
    fields: { profilePicture: 'basic' },
    id: memberId,
    useQueryOptions: { enabled: !!memberId },
  })

  const [generatedQuery, setGeneratedQuery] = useState('')

  let defaultIndex = 0
  const tabNames = ['all', 'post', 'member', 'space']
  if (type) {
    defaultIndex = tabNames.indexOf(type) !== -1 ? tabNames.indexOf(type) : 0
  }

  const replacePath = ({
    query: newQuery,
    type: newType,
    spaceId: newSpaceId,
    memberId: newMemberId,
    date: newDate,
  }: {
    query?: string
    type?: string
    spaceId?: string
    memberId?: string
    date?: string
  }) => {
    const queryParams: Record<string, string> = { query: newQuery || query }
    if (newType || type) queryParams.type = newType || type
    if (newSpaceId || spaceId) queryParams.space = newSpaceId || spaceId
    if (newMemberId || memberId) queryParams.member = newMemberId || memberId
    if (newDate || date) queryParams.date = newDate || date

    replace(generatePath(SystemSpaceSlug.SEARCH, null, queryParams))
  }

  useEffect(() => {
    let tql = query
    if (type) tql += ` +for:${type}`
    if (memberId) tql += ` +by:${memberId}`
    if (spaceId) tql += ` +in:${spaceId}`
    if (date)
      tql += ` +date>${dayjs()
        .add(-1 * parseInt(date, 10), 'day')
        .toISOString()}`
    setGeneratedQuery(tql)
  }, [type, memberId, spaceId, query, date])

  const debouncedQuery = useDebounce(generatedQuery, 300)
  const { data, isLoading } = useSearch({
    variables: {
      input: {
        query: debouncedQuery,
      },
    },
    fields: 'all',
    useQueryOptions: { enabled: !!generatedQuery },
  })

  if (!query) {
    return (
      <Container>
        <Card className="py-10">
          <EmptyState
            icon={<SearchIcon />}
            title="Nothing to search for yet"
            description="Please start typing in the search bar"
          />
        </Card>
      </Container>
    )
  }

  return (
    <Container>
      <div className="px-2 flex space-x-3">
        <div>
          <Button
            variant="outline"
            rounded
            leadingIcon={<ArrowLeftIcon />}
            as={Link}
            to="/"
          />
        </div>
        <PageHeader title={`Results for "${query}"`} />
      </div>
      <Tabs.Group
        defaultIndex={defaultIndex}
        onChange={tabIndex => {
          let date
          let type = null
          if (tabIndex > 0) type = tabNames[tabIndex]
          if (!type) {
            // reset date on combined results tab
            date = null
          }
          replacePath({
            type,
            memberId: null,
            spaceId: null,
            date,
          })
        }}
      >
        <Tabs.List variant="pills" className="px-2">
          <Tabs.Tab>All</Tabs.Tab>
          <Tabs.Tab>Posts</Tabs.Tab>
          <Tabs.Tab>Members</Tabs.Tab>
          <Tabs.Tab>Spaces</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panels>
          <Tabs.Panel>
            <SearchResult type={type} data={data} isLoading={isLoading} />
          </Tabs.Panel>

          <Tabs.Panel>
            <GridList columns={3} className="pb-5">
              <SpacePicker space={space} replacePath={replacePath} />
              <MemberPicker member={member} replacePath={replacePath} />
              <DatePicker date={date} replacePath={replacePath} />
            </GridList>
            <SearchResult type={type} data={data} isLoading={isLoading} />
          </Tabs.Panel>
          <Tabs.Panel>
            <div className="pb-5">
              <DatePicker date={date} replacePath={replacePath} />
            </div>
            <SearchResult type={type} data={data} isLoading={isLoading} />
          </Tabs.Panel>
          <Tabs.Panel>
            <div className="pb-5">
              <DatePicker date={date} replacePath={replacePath} />
            </div>
            <SearchResult type={type} data={data} isLoading={isLoading} />
          </Tabs.Panel>
        </Tabs.Panels>
      </Tabs.Group>
    </Container>
  )
}
